import PortalItem from "@/components/Assessments/PortalItem";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Category } from "@/schema/console/assessments/create/Category";
import { FormValues } from "@/schema/console/assessments/create/FormValues";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ArrowSortRegular } from "@fluentui/react-icons";
import { Close } from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ReorderCategoryDialogProps {
  onReorderFinish: (categories: Category[]) => void;
}

function ReorderCategoryDialog({
  onReorderFinish,
}: ReorderCategoryDialogProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const categories = useWatch({
    control,
    name: "categories",
  });
  const isEmpty = categories && categories.length === 0;

  const [items, setItems] = useState<Category[]>([]);
  useEffect(() => {
    setItems(categories);
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;
    setItems((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild disabled={isEmpty}>
        <Button variant="secondary" size="icon">
          <ArrowSortRegular fontSize={20} className="text-cyan-600" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("reorder-categories")}</DialogTitle>
        </DialogHeader>
        <DndContext
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="divide-y">
              {items.map((item) => (
                <PortalItem key={item.id} item={item} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <DialogFooter>
          <Close asChild>
            <Button variant="outline">{t("cancel")}</Button>
          </Close>
          <Close asChild>
            <Button
              onClick={() => {
                onReorderFinish(items);
              }}
            >
              {t("okay")}
            </Button>
          </Close>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReorderCategoryDialog;
